import { isAuthenticated } from '~/services/users'
import { useLoggedInUserStore } from '~/stores/loggedInUser'
import { usePageParamsStore } from '~/stores/pageParams'
import { useNuxtRouter } from '~/cynqueModules/nuxtRuntime'
import { isPublicRoute } from '~/cynqueModules/publicRoutes'

// GLOBAL middleware. Under Nuxt 2/Bridge this was scoped to default-layout pages
// via `layouts/default.vue`'s `middleware` option, but Nuxt 4 dropped
// layout-declared middleware. Making it global reproduces "every app page
// requires auth" — with the login route exempted so we don't redirect-loop it.
export default defineNuxtRouteMiddleware(async (to) => {
  const fn = '[middleware/authenticated.global.ts]'

  // Exempt the routes reachable while unauthenticated: signing in, and claiming
  // an invitation (whose link is the only thing a new user has — issue #132).
  // Case-insensitive: the routes are `/Login` and `/Claim`, but be tolerant of
  // the lower-case forms.
  if (isPublicRoute(to.path)) {
    return
  }

  const user = useLoggedInUserStore().getLoggedInUser
  if (!user) {
    console.debug(`${fn} No loggedInUser in store. Redirecting to /Login.`)
    return navigateTo('/Login')
  }
  console.debug(
    `${fn} There -IS- a loggedInUser in store. Not redirecting to /Login.`
  )
  try {
    await isAuthenticated()
    console.debug(`${fn} user is authenticated`)
  } catch (error) {
    console.debug(`${fn} user is -NOT- authenticated`)
    useLoggedInUserStore().clearLoggedInUser()
    return navigateTo('/Login')
  }
  console.debug(`${fn} route:`, to)
  if (to.path !== '/') {
    console.debug(`${fn} route.path !== '/'. No further middleware action.`)
    return
  }
  console.debug(
    `${fn} route.path === '/'. We do not have useful, viewable page at '/'.` +
      ` Redirecting to user's selected routeAfterLogin.`
  )
  const lastPath = usePageParamsStore().lastPath
  if (lastPath && user.settings?.routeAfterLogin === 'Last Page') {
    console.debug(`${fn} redirecting to '${lastPath}'`)
    return navigateTo({ path: lastPath })
  } else {
    // `routeAfterLogin` can hold values that are NOT route names — the 'Last Page'
    // sentinel (reached here when lastPath is empty), the '--' separator, or a stale
    // value from before a route rename. navigateTo({ name }) on an unregistered name
    // throws MATCHER_NOT_FOUND. Guard with hasRoute and fall back to Companies.
    //
    // useNuxtRouter(), NOT Nuxt's auto-imported useRouter(). Two reasons, and the
    // second is the one that matters: (1) the auto-import does not resolve under
    // vue-tsc here — the same reason the `navigateTo` calls above are already in
    // the ratchet baseline — so it would add a type error; (2) `useRouter()` is
    // `useNuxtApp()?.$router`, and useNuxtApp THROWS "[nuxt] instance unavailable"
    // in production when the instance is gone. This line sits after
    // `await isAuthenticated()`, which is exactly the post-await position
    // cynqueModules/nuxtRuntime.ts exists to survive. Throwing here would fail the
    // redirect after a SUCCESSFUL auth — the very bug being fixed.
    const routeAfterLogin = user.settings?.routeAfterLogin
    const router = useNuxtRouter()
    const redirectName =
      routeAfterLogin && router.hasRoute(routeAfterLogin)
        ? routeAfterLogin
        : 'Companies'
    console.debug(`${fn} redirecting to '${redirectName}'`)
    return navigateTo({ name: redirectName })
  }
})
